import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Settings {
  store_name: string;
  store_description: string;
  currency: string;
  email_notifications: boolean;
  order_notifications: boolean;
  hero_image_url: string;
  whatsapp_enabled: boolean;
  free_shipping_threshold: number;
  delivery_cost_per_order: number;
  wheel_enabled: boolean;
  wheel_win_percentage: number;
  popup_enabled: boolean;
  popup_product_id: string;
  popup_text: string;
  popup_min_cart_value: number;
}

export const useSettings = () => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<Settings> => {
      const { data, error } = await supabase
        .from('settings')
        .select('key, value');
      
      if (error) throw error;
      
      const settings: Settings = {
        store_name: 'أصيل',
        store_description: '',
        currency: 'IQD',
        email_notifications: true,
        order_notifications: true,
        hero_image_url: '',
        whatsapp_enabled: false,
        free_shipping_threshold: 75000,
        delivery_cost_per_order: 5000,
        wheel_enabled: false,
        wheel_win_percentage: 50,
        popup_enabled: false,
        popup_product_id: '',
        popup_text: '',
        popup_min_cart_value: 0,
      };

      data?.forEach((item: { key: string; value: string | null }) => {
        switch (item.key) {
          case 'store_name':
            settings.store_name = item.value || 'أصيل';
            break;
          case 'store_description':
            settings.store_description = item.value || '';
            break;
          case 'currency':
            settings.currency = item.value || 'IQD';
            break;
          case 'email_notifications':
            settings.email_notifications = item.value === 'true';
            break;
          case 'order_notifications':
            settings.order_notifications = item.value === 'true';
            break;
          case 'hero_image_url':
            settings.hero_image_url = item.value || '';
            break;
          case 'whatsapp_enabled':
            settings.whatsapp_enabled = item.value === 'true';
            break;
          case 'free_shipping_threshold':
            settings.free_shipping_threshold = item.value ? parseInt(item.value) : 75000;
            break;
          case 'delivery_cost_per_order':
            settings.delivery_cost_per_order = item.value ? parseInt(item.value) : 5000;
            break;
          case 'wheel_enabled':
            settings.wheel_enabled = item.value === 'true';
            break;
          case 'wheel_win_percentage':
            settings.wheel_win_percentage = item.value ? parseInt(item.value) : 50;
            break;
          case 'popup_enabled':
            settings.popup_enabled = item.value === 'true';
            break;
          case 'popup_product_id':
            settings.popup_product_id = item.value || '';
            break;
          case 'popup_text':
            settings.popup_text = item.value || '';
            break;
          case 'popup_min_cart_value':
            settings.popup_min_cart_value = item.value ? parseInt(item.value) : 0;
            break;
            break;
        }
      });

      return settings;
    },
  });
};

export const useUpdateSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: Partial<Settings>) => {
      const updates = Object.entries(settings).map(([key, value]) => ({
        key,
        value: typeof value === 'boolean' ? value.toString() : typeof value === 'number' ? value.toString() : value,
      }));

      for (const update of updates) {
        // Try update first
        const { data: existing } = await supabase
          .from('settings')
          .select('id')
          .eq('key', update.key)
          .single();
        
        if (existing) {
          const { error } = await supabase
            .from('settings')
            .update({ value: update.value })
            .eq('key', update.key);
          
          if (error) throw error;
        } else {
          // Insert if doesn't exist
          const { error } = await supabase
            .from('settings')
            .insert({ key: update.key, value: update.value });
          
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('تم حفظ الإعدادات بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ أثناء حفظ الإعدادات');
    },
  });
};
